import { useDeviceType } from '@cian/ui-kit';
import * as React from 'react';

import {
  UXFB_CLIENT_EVENTS_CONFIG_KEY,
  UXFB_EVENT_SCROLL_DEVELOPER_CATALOG,
  UXFB_PLATFORM,
  UXFB_SCROLL_DEVELOPER_CATALOG_COUNT,
} from '../../constants/uxfb';
import { useApplicationContext } from '../../utils/applicationContext';
import { sendUXSEventWhenReady } from '../../utils/uxs';

/**
 * Высота 1px: у ячейки нулевой высоты нулевая площадь пересечения, и IntersectionObserver
 * в части браузеров не срабатывает. Список — таблица, поэтому сентинел — строка `<tr>`.
 */
const sentinelCellStyle: React.CSSProperties = { height: 1, padding: 0, border: 'none' };

/**
 * Невидимый сентинел после N-й карточки застройщика в выдаче: когда он попадает во вьюпорт
 * (пользователь доскроллил ≥ N застройщиков), шлёт UXFB-событие `scroll_developer_catalog`.
 * По сценарию событие только десктопное. Одно событие на загрузку страницы (dedupeKey) —
 * переходы по пагинации и ремаунты дублей не дают.
 */
export const ScrollDeveloperCatalogUxfbTracker: React.FC = () => {
  const { config } = useApplicationContext();
  const deviceType = useDeviceType();
  const sentinelRef = React.useRef<HTMLTableRowElement | null>(null);

  React.useEffect(() => {
    const sentinel = sentinelRef.current;

    if (
      deviceType !== 'desktop' ||
      config.get<boolean>(UXFB_CLIENT_EVENTS_CONFIG_KEY) === false ||
      !sentinel ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return;
    }

    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) {
        return;
      }

      observer.disconnect();

      sendUXSEventWhenReady(
        UXFB_EVENT_SCROLL_DEVELOPER_CATALOG,
        { platform: UXFB_PLATFORM, count: String(UXFB_SCROLL_DEVELOPER_CATALOG_COUNT) },
        { dedupeKey: UXFB_EVENT_SCROLL_DEVELOPER_CATALOG },
      );
    });

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [config, deviceType]);

  return (
    <tr ref={sentinelRef} aria-hidden data-testid="ScrollDeveloperCatalogUxfbTracker">
      <td colSpan={5} style={sentinelCellStyle} />
    </tr>
  );
};
